import React, { useState, useEffect, useCallback } from 'react';
import _ from 'lodash';
import { createIntl, createIntlCache, RawIntlProvider, IntlConfig, IntlShape } from 'react-intl';
import { OutputChannels, setLocale as setLocaleAction, Locale } from 'src/modules/locale';
import { useRouter } from 'next/router';
import cookie from 'js-cookie';
import {
  getMessages,
  getLocalizedObject as getLocalizedObjectUtil,
  getLocalizedObjects as getLocalizedObjectsUtil,
} from '../../utils';
import { connect } from 'react-redux';
import { BaseStore } from 'src/store';
import {
  getOutputChannel,
  getDomainsUrls,
} from 'src/modules/locale/components/Provider/outputChannel';
import { EnabledCookieGroups } from 'src/modules/cookies';
import { AppEnvironments } from 'src/utils/types/env';

type localeType = IntlConfig['locale'];
type messagesType = IntlConfig['messages'];

export interface LocaleContextProps {
  setLocale: (locale: localeType) => void;
  getLocalizedPropertyValue: (key: string, object: any) => string;
  getLocalizedObject<T = { [s: string]: any }>(object: { [s: string]: any }): T;
  getLocalizedObjects<T = { [s: string]: any }>(object: { [s: string]: any }): T[];
  locale: localeType;
  messages: messagesType;
  intl: IntlShape;
  europe_url: string;
  australia_url: string;
  usa_url: string;
  outputChannel: OutputChannels;
  origin: string;
  appEnv: AppEnvironments;
}

interface LocaleProviderProps extends Pick<LocaleContextProps, 'locale' | 'messages'> {
  setLocaleAction: any;
  children: any;
  outputChannel: string;
  origin: string;
  enabledCookieGroups: EnabledCookieGroups;
  appEnv: AppEnvironments;
}

const LocaleContext = React.createContext<any>({});

export const useLocale = () => React.useContext<LocaleContextProps>(LocaleContext);

const cache = createIntlCache();

const LocaleProvider: React.FC<LocaleProviderProps> = (props) => {
  const router = useRouter();
  const {
    children,
    locale,
    messages,
    setLocaleAction,
    origin,
    enabledCookieGroups,
    appEnv,
  } = props;
  const outputChannel = getOutputChannel();
  const { europe_url, australia_url, usa_url } = getDomainsUrls();
  const [cookieLocaleValue, setCookieLocaleValue] = useState('');
  const [intl, setIntl] = useState(createIntl({ locale, messages }, cache));
  const { locale: routerLocale = 'en' } = router;

  const setLocale = useCallback(
    async (nextLocale: localeType) => {
      // keeping this log here to troubleshoot locale issues with SSG
      console.log('___ setLocale()', nextLocale);

      setLocaleAction(nextLocale as Locale);
      const nextMessages = await getMessages(nextLocale);
      setIntl(createIntl({ locale: nextLocale, messages: nextMessages }, cache));
      setCookieLocaleValue(nextLocale);
    },
    [setLocaleAction]
  );

  const getLocalizedPropertyValue = (key: string, object: any) => {
    return object[`${key}_${locale}`];
  };

  const getLocalizedObject = (object: { [s: string]: any }) => {
    return getLocalizedObjectUtil(object, intl.locale, outputChannel as string);
  };

  const getLocalizedObjects = (objects: { [s: string]: any }[]) => {
    return getLocalizedObjectsUtil(objects, intl.locale, outputChannel as string);
  };

  useEffect(() => {
    if (enabledCookieGroups.initial && cookieLocaleValue) {
      cookie.set('locale', cookieLocaleValue, { expires: 365 });
      cookie.set('NEXT_LOCALE', cookieLocaleValue, { expires: 365 });
    }
  }, [cookieLocaleValue, enabledCookieGroups.initial]);

  useEffect(() => {
    setIntl(createIntl({ locale, messages }, cache));
  }, [locale, messages]);

  useEffect(() => {
    let initialRegion: Locale;

    switch (outputChannel) {
      case OutputChannels.AU:
        initialRegion = Locale.AU;
        break;
      case OutputChannels.US:
        initialRegion = Locale.US;
        break;
      default:
      case OutputChannels.DE:
        initialRegion = routerLocale === 'de' ? Locale.DE : Locale.EN;
        break;
    }
    setLocaleAction(initialRegion);
  }, [outputChannel, routerLocale, setLocaleAction]);

  return (
    <LocaleContext.Provider
      value={{
        setLocale,
        getLocalizedPropertyValue,
        getLocalizedObject,
        getLocalizedObjects,
        locale: intl ? intl.locale : null,
        intl,
        europe_url,
        australia_url,
        usa_url,
        outputChannel,
        origin,
        appEnv,
      }}>
      <RawIntlProvider value={intl}>{children}</RawIntlProvider>
    </LocaleContext.Provider>
  );
};

const mapStateToProps = (state: BaseStore) => {
  return {
    outputChannel: state.locale.onlineChannel,
    enabledCookieGroups: state?.cookies?.enabledCookieGroups,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    setLocaleAction: (locale) => dispatch(setLocaleAction(locale)),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(LocaleProvider);
